// src/pages/ControlPanel.jsx
// Control Panel (Super Admins only, 3.9): the settings that apply to the
// whole system. Sections, each with its own address:
//   /portal/control-panel/accounts   Accounts: client accounts, their numbers
//     and who gets their faxes (3.10)
//   /portal/control-panel/roles      Roles & Access (per account)
//   /portal/control-panel/billing    Billing: rates per account
//   /portal/control-panel/templates  Email Templates and Announcements
//   /portal/control-panel/email-log  Email Log (3.59.0): every email sent,
//     the Amazon SES / Gmail switch, addresses that bounced
//   /portal/control-panel/retries    Automatic Retries: master ON/OFF switch,
//     pending scheduled retries (cancellable), exhausted/cancelled chains
//     that can be given one extra attempt, and faxes an automatic retry
//     delivered (last 30 days)
//   /portal/control-panel/retention  Data Retention (3.13.1): fax records
//     kept 24 months, daily clean-up on/off, what's next, run history
//   /portal/control-panel/rejected-emails, /blocked-senders,
//     /blocked-requests (3.14.0: their own sections; failed outgoing and
//     incoming moved to the Logs tab)
// People, the Activity log and Reminders moved to the Users tab (Users.jsx).
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from '../components/Notifications';
import { API_BASE } from '../firebase/config';
import SectionTabs from '../components/SectionTabs';
import Accounts from './Accounts';
import RolesAccess from './RolesAccess';
import Billing from './Billing';
import EmailTemplates from './EmailTemplates';
import Retention from './Retention';
import Automations from './Automations';
import EmailLog from './EmailLog';
import EmailRules from './EmailRules';
import Deployments from './Deployments';
import AppVersions from './AppVersions';
import Sop from './Sop';
import ShowSelect from '../components/ShowSelect';
import MultiSearchSelect from '../components/MultiSearchSelect';
import Loader from '../components/Loader';
import Emo from '../components/Emo';

// 3.59.1 (owner): the Email Templates group is Email Settings, Email Log first.
// Console tabs (3.29.1): Clients & Billing (with Contact Lookups; 3.29.2 also
// Fax Settings = the retry switches), Email Templates, Security, Data
// Retention, Automations. "Fax &
// Email Settings" was split up; each page keeps its address. Three groups
// since 3.18.5; older addresses still open them (CONTROL_ALIASES).
// 3.49.0: Security, Cost Analysis and Health became their own Super Admin
// pages (left strip); their old addresses open those pages (tabRoutes.js)
const G_CLIENTS = 'Clients & Billing', G_EMAIL = 'Email Settings',
  G_RETENTION = 'Data Retention', G_AUTOMATION = 'Automations', G_DEPLOY = 'Deployments', G_SOP = 'SOP', G_APPS = 'Apps';
export const CONTROL_SECTIONS = [
  { id: 'clients',   label: 'Clients',         icon: '🏢', group: G_CLIENTS },
  // the retry switches; here since 3.29.2; 3.61.0 (owner): its own name ("Fax Settings" is the client's tab in Settings) and a matching link
  { id: 'retry-switches', label: 'Retry Switches', icon: '🔁', group: G_CLIENTS },
  { id: 'contact-lookups', label: 'Contact Lookups', icon: '📞', group: G_CLIENTS },   // 3.26.0; here since 3.29.1
  { id: 'roles',     label: 'Roles & Access',  icon: '🛡️', group: G_CLIENTS },
  { id: 'billing',   label: 'Billing',         icon: '💳', group: G_CLIENTS },
  { id: 'email-log', label: 'Email Log',       icon: '📬', group: G_EMAIL },   // 3.59.0; first since 3.59.1
  { id: 'email-rules', label: 'Email Rules',   icon: '📮', group: G_EMAIL },   // 3.60.1 (owner): the Gmail card + the clean-up log
  { id: 'templates', label: 'Email Templates', icon: '✉️', group: G_EMAIL },
  { id: 'retention', label: 'Data Retention',  icon: '🗄️', group: G_RETENTION },
  { id: 'automations', label: 'What Runs on Its Own', icon: '🤖', group: G_AUTOMATION },   // 3.28.0
  { id: 'deployments', label: 'Deployments', icon: '🚀', group: G_DEPLOY },   // 3.60.4 (owner)
  { id: 'sop', label: 'Super Admin SOP', icon: '📖', group: G_SOP },   // 3.62.0 (owner)
  { id: 'app-versions', label: 'Apps', icon: '📱', group: G_APPS },   // 3.89.0 (owner)
];
// The main tabs (3.18.6): each group, with its sections in a row under it
const CONTROL_GROUPS = [
  { name: G_CLIENTS, icon: '🏢', about: 'Your clients and their fax numbers, automatic retries on or off, phone-listing lookups for contacts, what each role can do at each client, and how each client is billed.' },
  { name: G_EMAIL, icon: '✉️', about: 'Every email the portal sends and how it is sent (Amazon SES or Gmail), the rules for the fax mailboxes and what their clean-up deleted, the emails\' wording, and announcements.' },
  { name: G_RETENTION, icon: '🗄️', about: 'How long fax records are kept, and what was deleted.' },
  { name: G_AUTOMATION, icon: '🤖', about: 'Everything that runs on its own (scheduled jobs, the email gateways, deletion rules), where it runs, what it needs, and when it last ran.' },
  { name: G_SOP, icon: '📖', about: 'The A-to-Z guide to how the Fax Portal works and how to run it, so a new Super Admin can take over: 13 chapters, search, Download PDF and short videos. Updated in the same release as any change it covers.' },
  { name: G_DEPLOY, icon: '🚀', about: 'How the Fax Portal has been built: every release and deployment, kept current by each deploy.' },
  { name: G_APPS, icon: '📱', about: 'Every app and the version it is on.' },
];
const CONTROL_ALIASES = { accounts: 'clients', retries: 'retry-switches', 'fax-retries': 'retry-switches' };   // old links still open it

export default function ControlPanel() {
  return (
    <SectionTabs tabId="control" title="⚙️ Console" sections={CONTROL_SECTIONS} groups={CONTROL_GROUPS} aliases={CONTROL_ALIASES}>
      {sub => (
        <>
          {sub === 'clients'   && <Accounts />}
          {sub === 'roles'     && <RolesAccess />}
          {sub === 'billing'   && <Billing />}
          {sub === 'templates' && <EmailTemplates />}
          {sub === 'email-log' && <EmailLog />}
          {sub === 'email-rules' && <EmailRules />}
          {sub === 'retry-switches' && <AutomaticRetries />}
          {sub === 'retention' && <Retention />}
          {sub === 'automations' && <Automations />}
          {sub === 'deployments' && <Deployments />}
          {sub === 'app-versions' && <AppVersions />}
          {sub === 'sop' && <Sop />}
          {sub === 'contact-lookups' && <ContactLookups />}
        </>
      )}
    </SectionTabs>
  );
}

function AutomaticRetries() {
  const { getToken }            = useAuth();
  const { confirm }             = useNotify();
  const [enabled, setEnabled]   = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy]         = useState('');
  const [error, setError]       = useState('');
  const [notice, setNotice]     = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const token = await getToken();
      const res   = await fetch(`${API_BASE}/portal/control/retries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      setEnabled(json.enabled);
      setAccounts(json.accounts || []);
    } catch (e) {
      setError(e.message);
    }
  }, [getToken]);

  useEffect(() => { load(); }, [load]);

  // One account's switch (3.14.0)
  async function toggleAccount(a) {
    const on = !a.enabled;
    if (!(await confirm({ title: `${on ? 'Turn on' : 'Turn off'} automatic retries for ${a.name}?`,
      message: on ? 'Its failed faxes will be retried automatically again (pending ones go out when due).'
                  : 'No new retries for this client; its pending ones wait until you turn it back on.',
      confirmLabel: on ? 'Turn on' : 'Turn off', danger: !on }))) return;
    setBusy(`acc:${a.id}`); setError(''); setNotice('');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/portal/control/retry-toggle-account`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId: a.id, enabled: on }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Action failed');
      setNotice(`Automatic retries ${on ? 'on' : 'off'} for ${a.name}.`);
      await load();
    } catch (e) { setError(e.message); }
    setBusy('');
  }

  async function post(path, confirmMsg, busyKey) {
    if (confirmMsg && !(await confirm({ title: 'Are you sure?', message: confirmMsg, confirmLabel: 'Yes, continue' }))) return;
    setBusy(busyKey);
    setError('');
    setNotice('');
    try {
      const token = await getToken();
      const res   = await fetch(`${API_BASE}${path}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: path.includes('retry-toggle') ? JSON.stringify({ enabled: !enabled }) : undefined,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Action failed');
      setNotice('Done.');
      await load();
    } catch (e) {
      setError(e.message);
    }
    setBusy('');
  }

  return (
    <div>
      <div style={S.header}>
        <div />
        <button onClick={load} style={S.refreshBtn}><Emo e="↻" /> Refresh</button>
      </div>

      {error  && <div style={S.errorBanner}>{error}</div>}
      {notice && <div style={S.noticeBanner}>{notice}</div>}

      {/* Master switch */}
      <div style={S.switchCard}>
        <div>
          <div style={S.switchTitle}>Fax Retries: all clients</div>
          <div style={S.switchSub}>
            {enabled
              ? 'ON — a fax that fails for a temporary reason is retried once (twice for ★ priority numbers), in each client’s office hours (standard: weekdays 8 AM–5 PM Eastern).'
              : 'OFF — no new retries are scheduled; pending ones wait until re-enabled.'}
          </div>
        </div>
        <button
          disabled={busy === 'toggle'}
          onClick={() => post('/portal/control/retry-toggle',
            enabled
              ? 'Turn OFF automatic retries? Failed faxes will go straight to a final failure notice.'
              : 'Turn automatic retries back ON?',
            'toggle')}
          style={{ ...S.toggleBtn, background: enabled ? 'var(--c-2e7d32)' : 'var(--c-b71c1c)' }}>
          {enabled ? 'ON' : 'OFF'}
        </button>
      </div>

      {/* Each client's own switch (3.14.0); 3.18.8: a compact table with
          search and Show, so it stays short as clients are added */}
      <div style={S.accountsCard}>
        <div style={S.switchTitle}>By client</div>
        <div style={S.switchSub}>Each client can be switched on or off on its own. {enabled ? '' : 'The switch above is off, so no client gets retries right now.'}</div>
        <ClientSwitches accounts={accounts} enabled={enabled} busy={busy} onToggle={toggleAccount} />
      </div>

      {/* 3.29.2: the retry lists and ★ priority numbers moved to Settings →
          Fax Retries (top menu), where every client sees its own */}
      <div style={{ ...S.switchCard, display: 'block' }}>
        <div style={S.switchTitle}>Retry lists and priority numbers</div>
        <div style={S.switchSub}>Scheduled, exhausted and delivered retries, and each client's ★ priority numbers, are in <strong>Settings → Fax Settings → Fax Retries</strong> in the top menu. Clients see their own there; on “All clients” you see every client's.</div>
      </div>
    </div>
  );
}

const RETRY_SHOW = ['10', '20', '30', '40', '50'];

// Console → Contact Lookups (3.26.0): the phone-listing lookup of each new
// fax number ($0.003 at Telnyx, once per number), on or off for every
// client and per client
function ContactLookups() {
  const { getToken } = useAuth();
  const { confirm } = useNotify();
  const [data, setData]   = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy]   = useState('');
  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/portal/control/contact-lookups`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Couldn't load the settings.");
      setData(j); setError('');
    } catch (e) { setError(e.message); }
  }, [getToken]);
  useEffect(() => { load(); }, [load]);
  async function save(body, key, question) {
    if (question && !(await confirm({ title: 'Contact lookups', message: question, confirmLabel: 'Yes' }))) return;
    setBusy(key);
    try {
      const r = await fetch(`${API_BASE}/portal/control/contact-lookups`, { method: 'POST', headers: { Authorization: `Bearer ${await getToken()}`, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Couldn't save that.");
      await load();
    } catch (e) { setError(e.message); }
    setBusy('');
  }
  if (!data) return error ? <div style={S.errorBanner}>{error}</div> : <Loader />;
  const st = data.stats;
  return (
    <div>
      {error && <div style={S.errorBanner}>{error}</div>}
      <div style={S.switchCard}>
        <div>
          <div style={S.switchTitle}>Contact lookups: all clients</div>
          <div style={S.switchSub}>
            {data.enabled
              ? `ON — each fax number used for the first time is looked up once in the phone network ($${st.pricePerLookup} at Telnyx) and its listing shown in Contacts.`
              : 'OFF — new numbers aren\'t looked up; numbers already looked up keep their listing. Turning it back on starts from then (numbers that came in while it was off aren\'t looked up).'}
          </div>
        </div>
        <button disabled={busy === 'all'} onClick={() => save({ enabled: !data.enabled }, 'all', data.enabled ? 'Turn OFF contact lookups for every client?' : 'Turn contact lookups back ON for every client?')}
          style={{ ...S.toggleBtn, background: data.enabled ? 'var(--c-2e7d32)' : 'var(--c-b71c1c)' }}>{data.enabled ? 'ON' : 'OFF'}</button>
      </div>
      <div style={{ ...S.switchCard, display: 'block' }}>
        <div style={S.switchTitle}>So far</div>
        <div style={S.switchSub}>
          {st.lookedUp} numbers looked up (about ${(st.lookedUp * st.pricePerLookup).toFixed(2)}), {st.withListing} with a phone listing, {st.lookedUp - st.withListing} not listed.
          {' '}This month: {st.thisMonth} (about ${(st.thisMonth * st.pricePerLookup).toFixed(2)}).
        </div>
      </div>
      <div style={S.accountsCard}>
        <div style={S.switchTitle}>By client</div>
        <div style={S.switchSub}>A client that's off has no new numbers looked up for it. {data.enabled ? '' : 'The switch above is off, so no numbers are looked up right now.'}</div>
        <ClientSwitches accounts={data.accounts} enabled={data.enabled} busy={busy}
          onToggle={a => save({ accountId: a.id, enabled: !a.enabled }, `acc:${a.id}`)} />
      </div>
    </div>
  );
}

function ClientSwitches({ accounts, enabled, busy, onToggle }) {
  const [terms, setTerms] = useState([]);
  const [showN, setShowN] = useState('10');
  const ts = terms.map(t => String(t).toLowerCase());
  const matching = !ts.length ? accounts : accounts.filter(a => ts.some(t => a.name.toLowerCase().includes(t)));
  const shown = matching.slice(0, Number(showN) || 10);
  return (
    <>
      <div style={{ ...S.listBar, marginTop: 12 }}>
        <div style={{ minWidth: 240 }}>
          <MultiSearchSelect values={terms} onChange={setTerms} options={accounts.map(a => a.name)} placeholder="Search client" />
        </div>
        <ShowSelect value={showN} onChange={setShowN} options={RETRY_SHOW} groupStyle={S.listLabel} labelStyle={{}} style={S.listSelect} />
        <span style={S.accountSmall}>{shown.length < matching.length ? `${shown.length} of ${matching.length}` : `${matching.length} client${matching.length === 1 ? '' : 's'}`}</span>
      </div>
      <table style={{ ...S.table, marginTop: 8 }}>
        <tbody>
          {shown.map(a => (
            <tr key={a.id} style={{ borderBottom: '1px solid var(--c-eceff1)' }}>
              <td style={{ ...S.td, padding: '8px 4px' }}><strong>{a.name}</strong> <span style={S.accountSmall}>{a.type === 'individual' ? 'Individual' : 'Business'}</span></td>
              <td style={{ ...S.td, padding: '8px 4px', textAlign: 'right' }}>
                <button disabled={busy === `acc:${a.id}`} onClick={() => onToggle(a)}
                  style={{ ...S.toggleBtn, padding: '5px 14px', fontSize: 12, background: a.enabled ? (enabled ? 'var(--c-2e7d32)' : 'var(--c-90a4ae)') : 'var(--c-b71c1c)' }}
                  title={a.enabled ? 'On: click to turn off for this client' : 'Off: click to turn on for this client'}>
                  {a.enabled ? 'ON' : 'OFF'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

const S = {
  header      : { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  refreshBtn  : { padding: '8px 16px', background: 'var(--c-37474f)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  errorBanner : { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '12px 18px', borderRadius: 8, marginBottom: 16, fontSize: 14 },
  noticeBanner: { background: 'var(--c-e8f5e9)', color: 'var(--t-2e7d32)', padding: '12px 18px', borderRadius: 8, marginBottom: 16, fontSize: 14 },
  accountsCard: { background: 'var(--c-ffffff)', borderRadius: 12, padding: '18px 24px', marginBottom: 28, boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--c-e0f2f1)' },
  accountList : { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10, marginTop: 12 },
  accountRow  : { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '8px 12px', border: '1px solid var(--c-eceff1)', borderRadius: 8 },
  accountSmall: { fontSize: 11, color: 'var(--t-78909c)', fontWeight: 400 },
  listBar     : { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 },
  listLabel   : { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--t-607d8b)', fontWeight: 600 },
  listSelect  : { padding: '6px 10px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', fontSize: 13, fontFamily: 'inherit', background: 'var(--c-ffffff)' },
  switchCard  : { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--c-ffffff)', borderRadius: 12, padding: '18px 24px', marginBottom: 28, boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--c-e0f2f1)' },
  switchTitle : { fontSize: 16, fontWeight: 700, color: 'var(--t-00695c)', marginBottom: 4 },
  switchSub   : { fontSize: 13, color: 'var(--t-78909c)' },
  toggleBtn   : { padding: '10px 26px', color: 'var(--t-ffffff)', border: 'none', borderRadius: 20, cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'inherit', minWidth: 80 },
  sectionTitle: { color: 'var(--t-00695c)', fontSize: 16, fontWeight: 700, margin: '0 0 6px' },
  sectionSub  : { color: 'var(--t-78909c)', fontSize: 13, margin: '0 0 12px' },
  tableWrap   : { background: 'var(--c-ffffff)', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)', marginBottom: 28 },
  table       : { width: '100%', borderCollapse: 'collapse' },
  thead       : { background: 'var(--c-00695c)' },
  th          : { padding: '12px 14px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  tr          : { borderBottom: '1px solid var(--c-f0f0f0)' },
  td          : { padding: '11px 14px', fontSize: 13, color: 'var(--t-333333)', verticalAlign: 'middle' },
  badge       : { padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' },
  reasonCode  : { fontSize: 12, color: 'var(--t-b71c1c)', background: 'var(--c-fce4ec)', padding: '2px 8px', borderRadius: 4 },
  cancelBtn   : { padding: '6px 14px', background: 'var(--c-b71c1c)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap' },
  retryBtn    : { padding: '6px 14px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap' },
  emptyCell   : { textAlign: 'center', padding: '36px 0' },
  emptyState  : { color: 'var(--t-90a4ae)', fontSize: 14 },
};
